import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import Button from "components/basic/button";
import Scrollbar from "components/basic/scrollbar";
import Typography from "components/basic/typography";

import MessageBubble from "../MessageBubble";
import { MessageView, SenderSide } from "../../../../types";

interface MessageThreadProps {
  messages: MessageView[];
  mySide: SenderSide;
  hasMore: boolean;
  loading: boolean;
  onLoadOlder: () => void;
}

const NEAR_BOTTOM_PX = 120;

const MessageThread: React.FC<MessageThreadProps> = ({
  messages,
  mySide,
  hasMore,
  loading,
  onLoadOlder,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("messages");
  const containerRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);

  const lastMessageId = messages.length
    ? messages[messages.length - 1].id
    : null;

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    nearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  // Follow the tail on new messages, but don't yank the user away from
  // older messages they scrolled up to read.
  useEffect(() => {
    const el = containerRef.current;
    if (el && nearBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lastMessageId]);

  return (
    <Scrollbar
      scrollRef={containerRef}
      onScroll={handleScroll}
      sx={{ flex: 1, minHeight: 0, py: 1 }}
    >
      {hasMore && (
        <Box display="flex" justifyContent="center" py={1}>
          <Button size="small" onClick={onLoadOlder} loading={loading}>
            {t("thread.load_earlier")}
          </Button>
        </Box>
      )}
      {messages.length === 0 && !loading ? (
        <Box p={4} textAlign="center">
          <Typography
            variant="body2"
            style={{ color: theme.palette.text.secondary }}
          >
            {t("thread.empty")}
          </Typography>
        </Box>
      ) : (
        messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            isMine={message.senderSide === mySide}
          />
        ))
      )}
    </Scrollbar>
  );
};

export default MessageThread;
